import { useCallback } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Typography
} from '@mui/material';
import FunctionsIcon from '@mui/icons-material/Functions';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export type BlockedGroupPoint = {
  label: string;
  hint: string;
};

export type ScriptFunctionGroup = {
  id: string;
  label: string;
  pointCount: number;
  /** Non-empty = the group cannot launch; entries carry the per-point fix hints. */
  blockedPoints?: BlockedGroupPoint[];
};

type Props = {
  groups: ScriptFunctionGroup[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  disabled?: boolean;
};

export const GroupPicker = ({
  groups,
  selectedIds,
  onChange,
  disabled = false
}: Props) => {
  const handleToggle = useCallback(
    (groupId: string) => {
      onChange(
        selectedIds.includes(groupId)
          ? selectedIds.filter(id => id !== groupId)
          : [...selectedIds, groupId]
      );
    },
    [selectedIds, onChange]
  );

  if (groups.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap'
      }}
    >
      <FunctionsIcon fontSize="small" color="primary" />
      {groups.map(group => {
        const blocked = group.blockedPoints ?? [];
        const isBlocked = blocked.length > 0;
        const control = (
          <FormControlLabel
            key={group.id}
            control={
              <Checkbox
                size="small"
                checked={!isBlocked && selectedIds.includes(group.id)}
                onChange={() => handleToggle(group.id)}
                disabled={disabled || isBlocked}
                sx={{ py: 0.25 }}
              />
            }
            label={
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
              >
                <Typography
                  variant="caption"
                  color={isBlocked ? 'text.disabled' : undefined}
                >
                  {`${group.label} (${group.pointCount})`}
                </Typography>
                {isBlocked && (
                  <WarningAmberIcon fontSize="inherit" color="warning" />
                )}
              </Box>
            }
            sx={{ mr: 0.5 }}
          />
        );

        if (!isBlocked) return control;

        return (
          <Tooltip
            key={group.id}
            title={
              <Box sx={{ maxWidth: 360 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  This group can&apos;t run:
                </Typography>
                {blocked.map(p => (
                  <Typography
                    key={p.label}
                    variant="caption"
                    component="div"
                    sx={{ mt: 0.5 }}
                  >
                    • {p.label} — {p.hint}
                  </Typography>
                ))}
              </Box>
            }
          >
            <span>{control}</span>
          </Tooltip>
        );
      })}
    </Box>
  );
};
